import { useMemo } from 'react';
import { useAssemblySteps } from './categoryBands';
import { usePartsRun, type PartItem } from './partsRun';
import { namedIn, withLetter } from './partsCatalogue';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  foldsOf, gestureIds, pieceIdByKey, pieceItemId, pieceNames, printOf, useStepsRun, type PartRole, type SubStep, type SubStepFitting,
  type UsedPart, subStepLabel,
} from './stepsRun';
import { subStepFamily, type SubStepFamily, type SubStepKind } from './subStepKinds';
import { renderSubStepText } from './subStepText';
import { useStepParts } from './stepPartsRun';
import { useCataloguedParts } from './photographedParts';
import { partDbSize } from './partIdentity';
import { useCatalogItems } from './catalogItems';
import { fittingAt, type Book, type Fitting } from './assemblyStore';

// WHAT PHASE 5 CAN BE POINTED AT: the manual's sub-steps, each with the parts it uses.
//
// The phases before this one already worked all of it out and it was going unread. Phase 3
// shares the supplied parts out among the steps; Phase 4 breaks a step into sub-steps and
// says which parts each one consumes. Between them, "which parts is this fitting about" is
// already answered for every sub-step in the manual, and the assembly phase was asking a
// person to find them in a list of everything.
//
// So this is the join, and it is only a join — nothing here decides anything. It turns three
// existing answers into one flat list of places the phase can stand.

// ONE PLACE THE PHASE CAN STAND: a sub-step of the manual, or a whole step that nobody has
// broken into sub-steps yet.
export type Slot = {
  // `${category}#${letter}`, which is what the
  // fitting stores and what survives a reload — a position in a list would move the day a
  // step is re-analysed with one sub-step more.
  key: string;
  label: string;
  // The step's printed number, for the label.
  step: number | null;
  // The sub-step's letter, null when the step has none analysed and the slot is the step.
  letter: string | null;
  // WHAT IT SAYS TO DO, in both languages, resolved by whoever draws it — the idiom the
  // reconciliation and the review already use, since this module has no language.
  //
  // It is the thing that makes the slot readable: "1A" names a position and says nothing,
  // while "insert the barrel nut (G) into the horizontal hole" IS the fitting the phase is
  // being asked to describe. A step slot borrows the step's own line, from the repartition.
  textEn: string;
  textFr: string;
  // AND THE SAME IN BEATS, drawn the same way, where Phase 4 cut it (see SubStep.beatsEn): what is
  // shown under the animation as it plays (see captionAt). Empty where it did not.
  beatsEn: string[];
  beatsFr: string[];
  // AND WHAT EACH BEAT NAMES, by item id: what pairs a beat with the part of the gesture it is
  // about, whatever order Phase 4 said them in (see captionAt).
  beatItems: number[][];
  // WHICH FAMILY OF GESTURE THIS SUB-STEP IS: a combining (two or three pieces leave as one) or a
  // placing (a part moved and fastened to nothing). Read off the sub-step's own kind, one of
  // five; where that is null — every sub-step analysed before the five were asked for — off
  // the ids the old movement clause gave, which is the same distinction stated the longer
  // way. Null when there is nothing to go on at all.
  kind: SubStepFamily | null;
  // Which of the five it is, as Phase 4 answered it — `kind` above is this read down to its
  // family. Null on a sub-step analysed before the five were asked for, and on a step slot.
  subKind: SubStepKind | null;
  // WHAT THE MOVEMENT IS SAID TO BE, already resolved: catalog item ids for the two parts.
  // Null where nothing was said, or where what was said names a part this document cannot
  // place.
  //
  // FROM PHASE 4: the roles it gives each part, or the old stored clause on a manual analysed
  // before roles were asked for. It fills a slot nobody has answered and never overwrites one.
  suggests: { moves: number | null; onto: number | null } | null;
  // AND THE TOOL THAT MAKES IT, as its index in the Phase 2 parts list — the form the fitting
  // stores and the selector is written in. Not part of `suggests`, because it is not a
  // suggestion: no sub-step uses two tools at once, so the one Phase 4 lists for this
  // sub-step is the answer, and Phase 5's call is no longer asked for it.
  //
  // The FIRST of them where an analysis has listed several, and null where it listed none —
  // which is the sub-step done by hand.
  tool: string | null;
  // AND THE SECOND, where Phase 4 listed two: the hex key driving 2-2's JCBC screws after the PVC
  // screwdriver turned the barrel nut. Played on the second hardware (see Fitting.nextToolId).
  nextTool: string | null;
  // THE SHOT IS NOT ONE OF THESE. A slot used to carry the camStart and camEnd the model had
  // chosen for it; every sub-step is watched from the one fixed viewpoint now (FIXED_CAM in
  // assemblyStore), so there is nothing per-slot to carry.
  // EVERYTHING THIS SLOT NEEDS IN HAND: the parts it uses and the tools it uses, in the
  // manual's own order, parts first. One list because Phase 4 shows one list — "parts &
  // tools needed" is a heading both phases now write, and it named different things in each.
  //
  // `tool` is what tells them apart, and it is not decoration: a tool is not fitted anywhere,
  // so it is never a candidate for the two part selectors, and it is drawn with the wrench
  // Phase 4 draws it with.
  //
  // `itemId` is null for a part the catalog has never seen or that has no photograph — kept
  // rather than dropped, so a part missing from the chips is visible as missing instead of
  // simply absent.
  parts: {
    itemId: number | null;
    label: string;
    tool: boolean;
    qty: number | null;
    // WHICH END OF THE FITTING, from Phase 4 — the same answer this phase's suggestion is now
    // read off (see roleSuggest). Drawn on the chip, because the chips are where a reader
    // checks the sentence against the parts, and this phase no longer has a selector saying
    // which of them moves.
    role: PartRole | null;
  }[];
  // WHAT THE GESTURE LEAVES: the piece it assembles, with the id it carries for the rest of
  // the manual. Null for a placing, which fastens nothing, and for a combining nobody named —
  // the same test the numbering applies (see pieceIdByKey, which is where the id comes from;
  // this module assigns none).
  //
  // NO COUNT: a gesture leaves exactly one piece.
  //
  // Named in BOTH languages like the sentence beside it, since this module has no language of
  // its own and whoever draws it does.
  output: { id: string; en: string; fr: string } | null;
  // WHETHER THIS GESTURE IS ONE OF SEVERAL IDENTICAL ONES, and which of them it is.
  //
  // A manual prepares four screws by sliding four washers onto them, one sub-step each: same
  // kind, same two parts, same roles, same sentence, four times. They are one movement, and
  // answering them one at a time meant describing that movement four times and watching it
  // four times — four chances to describe it four different ways.
  //
  // So the first of them is the LEADER and the rest are its repeats. Phase 5 asks its
  // question, positions its parts and takes its sign-off on the leader alone; standing on a
  // repeat shows the leader's animation and nothing to answer. `leader` is the leader's own
  // key — equal to this slot's key ON the leader, so a reader of this field never has to
  // special-case the first one — and `index` is which of the `count` this is, for the label.
  //
  // IT IS "THE SAME INPUTS", AND THAT IS THE WHOLE TEST. See repeatPrint, which writes a
  // gesture down so two can be compared. What it does NOT catch is a gesture repeated at
  // four different places on a growing assembly — driving the four screws home — because
  // each of those receives a different piece and is therefore a different animation. That is
  // the test being right rather than the test falling short: the same gesture at another
  // place on the chair is another thing to watch.
  //
  // NULL on a gesture nothing else in the manual repeats, and on a whole-step slot.
  // `mirror`: this repeat is on the OTHER SIDE from its leader — the right rail's barrel nuts after
  // the left's — and plays the leader's gesture reflected left to right (see Fitting.mirrorAll).
  repeat: { leader: string; index: number; count: number; mirror: boolean } | null;
  // WHAT THE GESTURE DOES, as a print (see gestureIds) — what a sign-off is given on and checked
  // against (see Fitting.signedFor). Null on a whole-step slot, which is several gestures.
  gesture: string | null;
  // A HARDWARE PREPARED IN THE HAND, AND WHERE IT IS SHOWN (see foldsOf): the key of the sub-step
  // that uses it, which plays it as its first half. Null on every other sub-step.
  foldsInto: string | null;
  // AND THE OTHER WAY: the preparations this sub-step plays first, in the manual's order. Their
  // sentences open this one's (`textEn`, `textFr`).
  preps: string[];
};

export const slotKey = (category: string, letter: string | null) =>
  `${category}#${letter ?? ''}`;

// THE FITTING THAT ANSWERS A SLOT: its own, or the leader's where it repeats one.
//
// Every reader of a stored fitting goes through here rather than reaching into `bySlot` with
// a slot key, because a repeat HAS no fitting of its own and never will — the sign-off, the
// progress bar and the clip a visitor is shown must all resolve it the same way, and three
// copies of `slot.repeat?.leader ?? slot.key` is exactly how two of them would stop.
export const fittingFor = (book: Book, slot: Slot): Fitting =>
  fittingAt(book, slot.repeat?.leader ?? slot.key);

// THE SLOT'S OWN PARTS, as Phase 4 names them today: catalog items and pieces, tools aside —
// a tool is never the part that moves nor the one it goes onto (see useDocTools).
export const slotPartIds = (slot: Slot | undefined): Set<number> => new Set(
  (slot?.parts ?? [])
    .filter((p) => !p.tool)
    .map((p) => p.itemId)
    .filter((id): id is number => id != null),
);

// WHETHER PHASE 4 HAS CHANGED THE GESTURE A FITTING ANSWERS since it was written: another print
// than the one it was signed off on, another kind, or a part it holds that the sub-step no longer
// names (or somebody unticked). The test the
// phase applies on arrival, adopting the kind and dropping the part (see AssemblyPhase), and
// SignOffSync applies to every sub-step at once — so a sign-off given on another gesture is
// withdrawn when the manual opens, not when somebody happens to open that sub-step.
//
// It happens because a fitting is filed by position (`slot.key`): re-run Phase 4 and cut a step
// into more sub-steps, and the answer to the old 1-10 stays at 1-10 under a sentence that is not
// its own.
//
// Only where Phase 4 has said: no kind on a sub-step analysed before kinds were asked for, and no
// parts on one whose parts have not landed — neither is a change.
export function gestureMoved(slot: Slot, f: Fitting): boolean {
  if (f.signedFor && slot.gesture && f.signedFor !== slot.gesture) return true;
  if (slot.kind && slot.kind !== f.kind) return true;
  const ids = slotPartIds(slot);
  if (!ids.size) return false;
  const gone = (id: number | null) => id != null && (!ids.has(id) || f.excluded.includes(id));
  return gone(f.movingId) || gone(f.targetId);
}

// A GESTURE WRITTEN DOWN SO THAT TWO OF THEM CAN BE COMPARED: which of the four kinds it is,
// what it takes with what each part is for, and what is in the hand. Two sub-steps with the
// same print are the same movement made twice.
//
// THE OUTPUT IS DELIBERATELY NOT IN IT. Four prepared screws are named "first", "second",
// "third" and "fourth" prepared screw and carry four ids — 101 to 104 — and that is exactly
// what the manual needs them to be: the fourth sub-step of the step picks up 104 and not 101.
// The names differ because the pieces differ; the GESTURE that makes them does not.
//
// NOTHING WITHOUT PARTS IS EVER A REPEAT. A sub-step analysed before the parts were asked for
// per gesture comes back with an empty list (see the note in the loop below), and matching
// those on "they both take nothing" would hand one sub-step's animation to another that has
// nothing whatever to do with it — a real pair of them sits in this manual's older runs.
const repeatPrint = (sub: SubStep): string | null => {
  const parts = sub.parts ?? [];
  if (!parts.length) return null;
  return JSON.stringify([
    sub.kind,
    // SORTED, because the print is about what the gesture takes and not about the order the
    // model happened to list it in. `consumed` rides along: a part counted somewhere else is
    // a different claim about the same id.
    parts
      .map((p) => `${p.id}|${p.qty ?? 1}|${p.role ?? ''}|${p.consumed === false ? 'no' : 'yes'}`)
      .sort(),
    [...(sub.tools ?? [])].sort(),
  ]);
};

// The parts of a slot, resolved from indices into the Phase 2 list to catalog items.
//
// A step's parts are stored as the part's INDEX in that list (see StepPart.id), so the join
// runs through the list itself. Matching a resolved part by object identity rather than by
// re-deriving the catalog id keeps ONE definition of "which catalog row is this part" — the
// one photographedParts already applies, including its fallback for a stale extraction.
function resolveParts(
  // `qty` is nullable rather than optional because a step's parts carry it that way and a
  // sub-step's carry it as absent — the same fact written two ways one phase apart.
  // A tool comes in as a bare id — it has no quantity and no role — which is why this takes
  // the loose shape rather than UsedPart itself.
  ids: (Partial<UsedPart> & { id: string })[],
  items: ReturnType<typeof usePartsRun>['items'],
  itemOf: Map<unknown, number>,
  nameOf: (index: number) => string,
) {
  const seen = new Set<string>();
  const out: Slot['parts'] = [];
  for (const p of ids) {
    if (seen.has(p.id)) continue;
    seen.add(p.id);
    const index = Number(p.id);
    const part = items[index];
    // A PIECE AN EARLIER SUB-STEP MADE is in no parts list, and is one of this sub-step's parts all
    // the same — the one it is done ONTO, as often as not. Under its own id (see pieceItemId).
    const piece = part ? null : pieceItemId(p.id);
    if (!part && piece == null) continue;
    // WHETHER IT IS A TOOL, off the parts list itself — the same flag the tool selector
    // filters on. Carried here rather than decided by the caller because a sub-step's tool
    // list and its parts list can name the same id, and what the thing IS does not depend on
    // which of the two lists it was reached through.
    // HOW MANY OF IT, carried through rather than dropped. "Insert TWO barrel nuts" is the
    // sentence, Phase 4 answers 2, and this phase drew one chip saying "Barrel nut (G)" —
    // which is not a shorter way of saying the same thing, it is a different answer to how
    // many, given under a heading the two pages share.
    //
    // Null where the sub-step handles the part without using it up (`consumed: false`) — the
    // side rail two nuts go into is not two side rails — and null for a tool, which is used
    // and not consumed. Same test Phase 4's own review applies, so the two rows agree.
    out.push({
      itemId: part ? itemOf.get(part) ?? null : piece,
      label: nameOf(index),
      tool: !!part?.tool,
      qty: !part || p.consumed === false || p.qty == null ? null : p.qty,
      role: p.role ?? null,
    });
  }
  return out;
}

// THE MANUAL'S TOOLS, for the third selector — and now for the viewer as well.
//
// STILL ADDRESSED BY ITS INDEX in the parts list, which is what the fitting stores and what
// the selector is written in: a tool needs no catalog row to be NAMED, and half the manuals'
// allen keys have none. What is new is that it may also be DRAWN, and a mesh is addressed by
// its catalog item — so the catalog id comes along beside the index rather than replacing it,
// null for every tool the Parts DB has never seen. The two answers are different questions
// about one thing and this is the only place that holds both.
//
// STILL THE WHOLE MANUAL HERE, while a sub-step's own tool is whatever Phase 4 listed for it.
// That is not the two disagreeing: the sub-step's tool is what the selector OPENS on, and this
// is what it can be changed to — a person looking at the diagram is the one who can see that
// the analysis left the screwdriver out, and narrowing this too would take away the only
// correction available when it does.
export type DocTool = {
  // The tool's index in the Phase 2 parts list — what the fitting stores.
  value: string;
  label: string;
  // Its catalog item, for the viewer to find a mesh by. Null where the Parts DB has never
  // seen this tool, which is most allen keys and is not an error: it is still a tool, still
  // named, and still the answer to "what makes this fitting" — it simply cannot be drawn.
  itemId: number | null;
  // What the manual says it measures, for putting that mesh into millimetres.
  dims: PartItem['size'];
};

export function useDocTools(docId: string): DocTool[] {
  const { items } = usePartsRun(docId);
  // NAMED IN THE LANGUAGE ON SCREEN. This module holds no words of its own — the sub-step
  // texts are handed out in both and resolved by whoever draws them — but a part's NAME is a
  // fact about the part rather than a string to translate, and picking which of its three to
  // show is a question only the toggle can answer.
  const { language } = useLanguage();
  const { parts: resolved } = useCataloguedParts(docId);
  // The database's sizes, for the parts this manual has not measured — see partDbSize.
  const { items: catalog } = useCatalogItems();
  return useMemo(
    () => {
      // The same join the slots use, by object identity rather than by re-deriving the
      // catalog id — one definition of "which catalog row is this part", see resolveParts.
      const itemOf = new Map<unknown, number>(resolved.map((p) => [p.part, p.itemId]));
      return items
        .map((p, i) => ({ p, i }))
        .filter(({ p }) => p.tool)
        .map(({ p, i }) => {
          const name = namedIn(p, language) || `#${i}`;
          return {
            value: String(i),
            label: withLetter(name, p.letter),
            itemId: itemOf.get(p) ?? null,
            // The database's size where the manual has none — a tool is the likeliest part in
            // the list to have never been measured here, and the likeliest to be in the
            // database already. See partDbSize.
            dims: partDbSize(catalog, itemOf.get(p) ?? null, p.size),
          };
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, resolved, language, catalog],
  );
}

// WHAT A MOVEMENT SAYS, TURNED INTO WHAT THIS DOCUMENT CAN DRAW: the two ids as catalog
// items, and the kind read off the ids where the word is missing. Its own hook because it has TWO callers that must agree exactly — the slots below,
// which resolve every sub-step's stored answer, and Phase 5's button, which resolves the one
// it has just been given. Held apart, a freshly fetched movement and the same movement read
// back after a reload would resolve by two rules.
export function useFittingResolver(docId: string) {
  const { items } = usePartsRun(docId);
  const { parts: resolved, loaded } = useCataloguedParts(docId);

  return useMemo(() => {
    const itemOf = new Map<unknown, number>(resolved.map((p) => [p.part, p.itemId]));

    // An id from the analysis, as a catalog item this document can actually place — or null.
    // Handed out as well as used here: Phase 5's call needs the same answer to describe a
    // part by its MESH, and two ways of turning an analysis id into a catalog row is exactly
    // the drift this hook exists to prevent.
    const asItem = (id: string | null | undefined): number | null => {
      if (!id) return null;
      const part = items[Number(id)];
      // A piece an earlier sub-step made, under its own id — see pieceItemId.
      return part ? itemOf.get(part) ?? null : pieceItemId(id);
    };
    const suggestOf = (f: SubStepFitting | null | undefined) => (f ? {
      moves: asItem(f.moves),
      onto: asItem(f.onto),
    } : null);
    // THE TWO ENDS OF THE FITTING, OFF PHASE 4'S OWN ANSWER. Each part it lists carries a
    // role — added or receiving (see PartRole) — and that is this question, answered by the
    // call that wrote the sentence with every part of the step in hand. Phase 5 used to ask a
    // second reader the same thing from one diagram; it is not asked any more.
    //
    // NULL WHERE NOBODY SAID, so the old answers below are still reached: every sub-step
    // analysed before `role` was asked for has none, and a manual is not re-analysed by being
    // looked at.
    const roleSuggest = (parts: UsedPart[] | undefined) => {
      const added = parts?.find((p) => p.role === 'added');
      const onto = parts?.find((p) => p.role === 'receiving');
      if (!added && !onto) return null;
      return { moves: asItem(added?.id), onto: asItem(onto?.id) };
    };
    // PLACING OR COMBINING, and THE SUB-STEP'S OWN KIND SAYS IT. One of four gestures, one of
    // which fasten nothing (see subStepKinds) — so this is not a second question with its own
    // answer any more, it is a reading of the one Phase 4 gives.
    //
    // Falling back to the ids where that kind is null, which is every manual until its steps
    // are re-analysed. They state the same distinction the longer way: something to be fitted
    // ONTO is a combining, a part that only moves is a placing. Read off the RAW ids and not
    // off `suggests`, which is them resolved against this document's catalog — a sub-step
    // joining two parts is a combining whether or not either has a mesh yet, and demoting it
    // to a placing because the Parts DB is behind would be this phase's shortcomings written
    // into the manual's own reading of itself.
    const kindOf = (
      subKind: SubStepKind | null,
      f: SubStepFitting | null | undefined,
    ): SubStepFamily | null => {
      const family = subStepFamily(subKind);
      if (family) return family;
      if (!f) return null;
      if (f.onto) return 'combining';
      return f.moves ? 'placing' : null;
    };

    // LOADED: the catalog and the photos this resolves through are in — until then every id is null.
    return { itemOf, asItem, suggestOf, roleSuggest, kindOf, loaded };
  }, [items, resolved, loaded]);
}

export function useAssemblySlots(docId: string): Slot[] {
  const { steps } = useAssemblySteps(docId);
  const { language } = useLanguage();
  const { items } = usePartsRun(docId);
  const { subSteps } = useStepsRun(docId);
  const { byStep, text: stepText } = useStepParts(docId);
  const { itemOf, suggestOf, roleSuggest, kindOf } = useFittingResolver(docId);

  return useMemo(() => {
    // THE PIECES THE MANUAL ASSEMBLES, numbered once for every phase that prints them — this
    // module assigns none of its own. See pieceIdByKey.
    const pieces = pieceIdByKey(steps, subSteps);
    // The same pieces, by id and named, for the two sentences above. Each language asks for
    // its own: the names come from the piece's bilingual output and the slot carries both.
    const gestures = gestureIds(steps, subSteps);
    const folds = foldsOf(steps, subSteps);
    const pieceName = pieceNames(steps, subSteps, 'en');
    const pieceNameFr = pieceNames(steps, subSteps, 'fr');
    const nameOf = (index: number) => {
      const p = items[index];
      // A piece, as its output chip names it: "102 – Rear frame with first rail positioned".
      const made = (language === 'fr' ? pieceNameFr : pieceName).get(String(index))?.name;
      if (!p) return made ? `${index} – ${made}` : `#${index}`;
      const name = namedIn(p, language) || `#${index}`;
      return withLetter(name, p.letter);
    };

    // EVERY SLOT IS A SUB-STEP OF THE MANUAL. There used to be one that was not: "0X", first
    // in the list, standing for no step at all — two dropdowns over the whole parts catalogue,
    // for trying a fitting the manual does not describe. It was the phase's original shape,
    // from before there were sub-steps to stand on, and everything since has been built to
    // exclude it: it could not be asked about, it restricted nothing, and half a dozen guards
    // existed to say "not this one". A scratchpad at the head of the manual's own sequence.
    //
    // Answers stored under it are left on disk rather than deleted. They are unreachable now,
    // and a migration that removed them would be destroying somebody's work to tidy a key.
    const out: Slot[] = [];
    // ONE PRINT PER SLOT, IN STEP WITH `out` — null for a whole-step slot, which is several
    // gestures and therefore not one that anything can repeat. Collected as the slots are
    // built rather than read back off them, because what a gesture takes is the sub-step's
    // own list (ids as Phase 4 wrote them) and a slot carries that resolved to catalog rows,
    // by which point two different parts with one mesh look like one part.
    const prints: (string | null)[] = [];
    // AND ITS SIDE, where the gesture works on one hand of a pair: the receiving part's, else the
    // added part's (see UsedPart.side). What tells a repeat on the other side from its leader.
    const sides: (string | null)[] = [];

    for (const band of steps) {
      const subs = subSteps[band.category] ?? [];
      const stepParts = byStep[band.category] ?? [];
      if (subs.length) {
        for (const sub of subs) {
          // WHAT THE SUB-STEP SAYS IT USES, and nothing else. It used to fall back to the
          // step's parts when a sub-step listed none, which was the wrong answer to the
          // right question: a sub-step with no parts is a sub-step whose analysis is
          // missing, and quietly handing over the step's twelve parts hides that behind a
          // row of chips that looks like an answer.
          //
          // Empty here has ONE cause worth knowing. The server keeps a sub-step part only
          // if the STEP's own list names it (_reconcile_substeps), and that list is Phase
          // 5's. So a step analysed before the repartition ran comes back with every
          // sub-step empty — re-running the analysis for that step is what fills them.
          // PHASE 4'S OLD STORED CLAUSE, where its roles are missing (see `suggests`).
          const said = sub.fitting;
          prints.push(repeatPrint(sub));
          sides.push(sub.parts.find((u) => u.role === 'receiving')?.side
            ?? sub.parts.find((u) => u.role === 'added')?.side ?? null);
          out.push({
            // The manual's own name for it: step number then its own, "1-2" — which is how
            // Phase 4 prints it and how the diagram is captioned.
            key: slotKey(band.category, sub.letter),
            label: subStepLabel(band.num, sub.letter),
            step: band.num,
            letter: sub.letter,
            // DRAWN OUT OF THE GESTURE'S OWN PARTS, not read off as prose: a sentence names
            // its parts by id in braces (see subStepText), and this phase shows it as written —
            // one sub-step, one sentence, never another's joined onto it.
            textEn: renderSubStepText(sub.textEn, sub.parts, items, pieceName, 'en'),
            textFr: renderSubStepText(sub.textFr, sub.parts, items, pieceNameFr, 'fr'),
            beatsEn: (sub.beatsEn ?? []).map((b) => renderSubStepText(b, sub.parts, items, pieceName, 'en')),
            beatsFr: (sub.beatsFr ?? []).map((b) => renderSubStepText(b, sub.parts, items, pieceNameFr, 'fr')),
            beatItems: (sub.beatsEn ?? []).map((b) => [...b.matchAll(/\{([^{}]+)\}/g)].flatMap(([, raw]) => {
              const id = raw.trim();
              const part = items[Number(id)];
              const item = part ? itemOf.get(part) : pieceItemId(id);
              return item != null ? [item] : [];
            })),
            kind: kindOf(sub.kind, said),
            // THE SUB-STEP'S OWN KIND, carried beside the family it resolves to. The phase
            // re-derives the family when a fresh movement answer lands (see askMovement), and
            // it has to start from the same place this did: a manual whose steps predate the
            // four kinds falls back on the answer's ids, and that fallback must read the NEW
            // answer rather than the family computed from the one it just replaced.
            subKind: sub.kind,
            output: (() => {
              const made = pieces.get(slotKey(band.category, sub.letter));
              return made ? { id: made.id, en: made.output.en, fr: made.output.fr } : null;
            })(),
            // Filled in below, once every slot exists: which gestures repeat which is a fact
            // about the manual as a whole and cannot be known while it is being walked.
            repeat: null,
            gesture: printOf(gestures.get(slotKey(band.category, sub.letter))),
            foldsInto: folds.get(slotKey(band.category, sub.letter)) ?? null,
            preps: [...folds].filter(([, user]) => user === slotKey(band.category, sub.letter)).map(([prep]) => prep),
            // PHASE 4'S ROLES FIRST, its old clause after them. Not merged: a `moves` from one
            // beside an `onto` from the other would be a movement neither described.
            suggests: roleSuggest(sub.parts) ?? suggestOf(said),
            // NOT FROM THE ANSWER — from the sub-step. Filtered by what the parts list calls
            // a tool, so an analysis that put a screw in the tool list prefills nothing.
            tool: (sub.tools ?? []).find((id) => items[Number(id)]?.tool) ?? null,
            nextTool: (sub.tools ?? []).filter((id) => items[Number(id)]?.tool)[1] ?? null,
            // PARTS AND TOOLS, the same union Phase 4 draws under the same heading. Its
            // sentence names the screwdriver, its analysis lists it, and this phase drew a
            // row that silently left it out — two pages claiming to show one list, one of
            // them a proper subset of the other, and nothing on either saying so.
            //
            // Parts first and tools after, which is the order Phase 4 uses; resolveParts
            // de-duplicates by id, so a tool that is also named as a part is one chip.
            parts: resolveParts(
              [...(sub.parts ?? []), ...(sub.tools ?? []).map((id) => ({ id }))],
              items, itemOf, nameOf,
            ),
          });
        }
      } else if (band.category in subSteps) {
        // ANALYSED, AND IT HAS NO GESTURES. Not a gap — an answer: a step whose whole content
        // is turning the workpiece over ("stand the chair upright on its legs") contains
        // nothing that joins anything, and Phase 4 is now told to return an empty list for it
        // rather than dress it up as a sub-step (see stepAnalyzePrompt).
        //
        // So there is nothing here to stand on and nothing to animate, and this step
        // contributes no slot at all. Its own instruction survives where it was transcribed,
        // at step level, so the manual still says to stand the chair up.
        //
        // NOT THE SAME AS THE BRANCH BELOW, and the difference is the whole reason this reads
        // the key rather than the length: a step nobody has analysed is unanswered, and an
        // unanswered step is still somewhere to stand.
      } else {
        // A step nobody has analysed into sub-steps is still a place to stand: Phase 3's
        // repartition knows what it adds, which is the coarser half of the same answer. The
        // alternative is a phase that shows nothing until Phase 4 has run over every step.
        prints.push(null);
        sides.push(null);
        out.push({
          key: slotKey(band.category, null),
          label: `${band.num}`,
          step: band.num,
          letter: null,
          // A STEP LEAVES NOTHING NAMEABLE. It is several gestures, and the pieces are what
          // its sub-steps leave — which is exactly what this slot exists for the absence of.
          output: null,
          // Several gestures is not one gesture, so there is nothing here for another slot to
          // be a second performance of.
          repeat: null,
          gesture: null,
          foldsInto: null,
          preps: [],
          beatsEn: [],
          beatsFr: [],
          beatItems: [],
          textEn: stepText[band.category]?.en || '',
          textFr: stepText[band.category]?.fr || '',
          // A step is several movements; it is not one kind of movement, nor one of the five.
          kind: null,
          subKind: null,
          // A step slot has no fitting of its own: the analysis answers per sub-step, and a
          // step is several movements rather than one.
          suggests: null,
          tool: null,
          nextTool: null,
          // A whole step is not a gesture, so it repeats nothing.
          parts: resolveParts(stepParts, items, itemOf, nameOf),
        });
      }
    }

    // AND WHICH OF THEM ARE THE SAME GESTURE, over the whole manual rather than within a step.
    //
    // MANUAL-WIDE ON PURPOSE. This chair prepares four screws in step 1 and two more in step
    // 2, and all six are the same washer going onto the same screw — grouped per step they
    // would be two runs of an identical movement, described twice and watched twice, which is
    // most of what this is here to stop. A gesture is the same gesture wherever the manual
    // gets round to it.
    //
    // THE FIRST ONE IN THE MANUAL'S OWN ORDER LEADS, which is the only choice a reader can
    // predict: the slots are built step by step and sub-step by sub-step, so the leader is the
    // one they reach first walking forwards.
    const runs = new Map<string, number[]>();
    prints.forEach((print, i) => {
      if (!print) return;
      const seen = runs.get(print);
      if (seen) seen.push(i);
      else runs.set(print, [i]);
    });
    for (const members of runs.values()) {
      // A gesture the manual makes once is not a repeat of anything, and saying "(1/1)" after
      // its name would be the app counting out loud for no reason.
      if (members.length < 2) continue;
      const leader = out[members[0]].key;
      members.forEach((member, n) => {
        const [here, first] = [sides[member], sides[members[0]]];
        out[member].repeat = {
          leader, index: n + 1, count: members.length, mirror: !!here && !!first && here !== first,
        };
      });
    }
    // A SUB-STEP THAT PLAYS A PREPARATION FIRST SAYS SO FIRST: "Slide the spring washer onto the
    // screw. Insert the prepared screw into the first hole…" — one screen, both halves.
    const byKey = new Map(out.map((slot) => [slot.key, slot]));
    for (const slot of out) {
      if (!slot.preps.length) continue;
      const first = slot.preps.map((key) => byKey.get(key)).filter((p): p is Slot => !!p);
      slot.textEn = [...first.map((p) => p.textEn), slot.textEn].filter(Boolean).join(' ');
      slot.textFr = [...first.map((p) => p.textFr), slot.textFr].filter(Boolean).join(' ');
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps, subSteps, byStep, stepText, items, itemOf, suggestOf, roleSuggest,
    kindOf, language]);
}
